import { parseISO } from 'date-fns';

import { PILOT_DURATION_BUSINESS_DAYS } from '@/sections/customerManagement/constants';
import { CustomerTypeValue } from '@/types';

export interface DateCalculationResult {
  startDate: string;
  endDate: string;
  wasAdjusted: boolean;
  adjustmentReason?: string;
}

export interface DateCalculationInput {
  customerType: CustomerTypeValue;
  requestedStartDate: string;
}

/**
 * Calculates subscription dates based on customer type and business rules
 */
export const calculateSubscriptionDates = ({
  customerType,
  requestedStartDate,
}: DateCalculationInput): DateCalculationResult => {
  const startDate = parseISO(requestedStartDate);
  let adjustedStart = new Date(startDate);
  let wasAdjusted = false;
  let adjustmentReason: string | undefined;

  // Apply customer type-specific start date rules
  if (customerType === 'PILOT') {
    const mondayAdjustment = adjustToNextMonday(adjustedStart);
    if (mondayAdjustment.wasAdjusted) {
      adjustedStart = mondayAdjustment.adjustedDate;
      wasAdjusted = true;
      adjustmentReason = 'PILOT customers must start on Monday';
    }
  }

  // Calculate end date based on customer type
  const endDate = calculateEndDate(customerType, adjustedStart);

  // For ENGAGEMENT, don't set an end date - leave it blank
  const endDateString =
    customerType === 'ENGAGEMENT' ? '' : formatDateForInput(endDate);

  return {
    startDate: formatDateForInput(adjustedStart),
    endDate: endDateString,
    wasAdjusted,
    adjustmentReason,
  };
};

/**
 * Adjusts date to next Monday if not already Monday
 */
export const adjustToNextMonday = (
  date: Date
): {
  adjustedDate: Date;
  wasAdjusted: boolean;
} => {
  const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday

  if (dayOfWeek === 1) {
    return { adjustedDate: new Date(date), wasAdjusted: false };
  }

  const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek) % 7;
  const adjustedDate = new Date(date);
  adjustedDate.setDate(adjustedDate.getDate() + daysUntilMonday);

  return { adjustedDate, wasAdjusted: true };
};

/**
 * Calculates end date based on customer type business rules
 */
export const calculateEndDate = (
  customerType: CustomerTypeValue,
  startDate: Date
): Date => {
  const endDate = new Date(startDate);

  switch (customerType) {
    case 'PILOT': {
      // 4 business weeks (PILOT_DURATION_BUSINESS_DAYS business days)
      // Starting Monday (day 1), ending Friday of week 4 (day PILOT_DURATION_BUSINESS_DAYS)
      let businessDaysAdded = 1; // Start date counts as day 1
      const targetBusinessDays = PILOT_DURATION_BUSINESS_DAYS;

      while (businessDaysAdded < targetBusinessDays) {
        endDate.setDate(endDate.getDate() + 1);
        const dayOfWeek = endDate.getDay();
        // Skip weekends (0 = Sunday, 6 = Saturday)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          businessDaysAdded++;
        }
      }
      break;
    }

    case 'FREEMIUM':
    case 'MANAGED':
    case 'SAAS':
      // 1 year duration minus 1 day
      endDate.setFullYear(endDate.getFullYear() + 1);
      endDate.setDate(endDate.getDate() - 1);
      break;

    case 'ENGAGEMENT':
      // No auto-calculation for engagement customers
      return new Date(startDate);

    default:
      return new Date(startDate);
  }

  return endDate;
};

/**
 * Formats date for HTML input field (YYYY-MM-DD)
 */
export const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Gets customer type-specific date calculation rules for display
 */
export const getCustomerTypeDateRules = (
  customerType: CustomerTypeValue
): string => {
  switch (customerType) {
    case 'PILOT':
      return `Start dates must be on a Monday. The program runs for 4 business weeks (${PILOT_DURATION_BUSINESS_DAYS} business days), ending on a Friday.`;
    case 'FREEMIUM':
    case 'MANAGED':
    case 'SAAS':
      return 'Subscription runs for 1 year from the start date.';
    case 'ENGAGEMENT':
      return 'Custom date range - no automatic calculations applied.';
    default:
      return '';
  }
};
